// Today — the home screen, and the answer to "what do I do now?".
//
// The panel used to open on a wall of charts, which tells you how the business
// did but not what it needs. This screen lists only things that are WAITING,
// in the order money moves: verify the payment → hand the part over → pay what
// we owe → chase what we're owed. Each card says what it is, how many, how much
// money is involved, how long the oldest one has waited, and takes you straight
// to the screen that clears it.
//
// When nothing is waiting it says so plainly. An empty queue is a result.

import React from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout.jsx';
import { PageHeader } from '../components/common/index.js';
import { useWorklist } from '../hooks/useWorklist';
import { WORK_QUEUES } from '../constants/navigation';
import { ROUTES } from '../constants/app.constants';
import {
  FiSun,
  FiCheckCircle,
  FiRefreshCw,
  FiArrowRight,
  FiClock,
  FiShoppingBag,
  FiTruck,
  FiUserPlus,
  FiAlertTriangle,
  FiBarChart2,
  FiKey,
} from 'react-icons/fi';

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return null;
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(a / 10000000).toFixed(2)} Cr`;
  if (a >= 100000) return `₹${(a / 100000).toFixed(2)} L`;
  return `₹${a.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

// act   = money is stuck until the admin does this
// pay   = we owe someone
// chase = someone owes us
// info  = worth knowing, not blocking
const TONES = {
  act:   { ring: 'border-violet-200', chip: 'bg-violet-600', soft: 'bg-violet-50', text: 'text-violet-700', btn: 'bg-violet-600 hover:bg-violet-700' },
  pay:   { ring: 'border-rose-200',   chip: 'bg-rose-600',   soft: 'bg-rose-50',   text: 'text-rose-700',   btn: 'bg-rose-600 hover:bg-rose-700' },
  chase: { ring: 'border-amber-200',  chip: 'bg-amber-500',  soft: 'bg-amber-50',  text: 'text-amber-700',  btn: 'bg-amber-500 hover:bg-amber-600' },
  info:  { ring: 'border-slate-200',  chip: 'bg-slate-500',  soft: 'bg-slate-50',  text: 'text-slate-600',  btn: 'bg-slate-700 hover:bg-slate-800' },
};

const ageLabel = (days) => {
  if (!days) return null;
  if (days === 1) return 'oldest waiting 1 day';
  return `oldest waiting ${days} days`;
};

const QueueCard = ({ queue, stat }) => {
  const t = TONES[queue.tone] || TONES.info;
  const Icon = queue.icon;
  const count = Number(stat?.count || 0);
  const money = fmtMoney(stat?.value);
  const days = Number(stat?.oldest_days || 0);
  const stale = days >= 3;

  return (
    <div className={`bg-white rounded-2xl border ${t.ring} shadow-sm p-5 flex flex-col`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${t.soft} flex items-center justify-center shrink-0`}>
          <Icon className={`w-5 h-5 ${t.text}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-black text-slate-900 leading-none tabular-nums">{count}</span>
            <h3 className="text-sm font-bold text-slate-700">{queue.title}</h3>
          </div>
          <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{queue.body}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {money && (
          <span className={`inline-flex items-center rounded-full ${t.soft} ${t.text} px-2.5 py-1 text-xs font-bold`}>
            {money}{queue.money ? ` ${queue.money}` : ''}
          </span>
        )}
        {days > 0 && (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
            stale ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500'
          }`}>
            {stale ? <FiAlertTriangle className="w-3 h-3" /> : <FiClock className="w-3 h-3" />}
            {ageLabel(days)}
          </span>
        )}
      </div>

      <Link
        to={queue.path}
        className={`mt-4 inline-flex items-center justify-center gap-2 rounded-xl ${t.btn} text-white text-sm font-bold py-2.5 transition-all`}
      >
        {queue.action}
        <FiArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

// Each number opens the rows behind it. A count you cannot click is a dead
// end — "7 inquiries came in" is only useful once you can see WHICH seven.
const TodaySnapshot = ({ today }) => {
  const cells = [
    { label: 'Sold today', value: today?.sold_count ?? 0, icon: FiShoppingBag,
      sub: fmtMoney(today?.sold_value) || '₹0', to: ROUTES.ORDERS, cta: 'See orders' },
    { label: 'Dispatched today', value: today?.dispatched ?? 0, icon: FiTruck,
      sub: 'handed over', to: ROUTES.DISPATCH, cta: 'See dispatch' },
    { label: 'New inquiries', value: today?.new_inquiries ?? 0, icon: FiUserPlus,
      sub: 'came in today', to: ROUTES.TODAY_INQUIRIES, cta: 'See who came in',
      accent: true },
  ];

  // Cars only earn a tile once there is something to say — a car went today,
  // or the car business is holding CAP's money. On a day with neither, an
  // extra "0 cars" box is noise on a screen whose whole point is signal.
  const carsSold = Number(today?.cars_sold_count || 0);
  const carsFloat = Number(today?.cars_fund_outstanding || 0);
  if (carsSold > 0) {
    cells.push({
      label: 'Cars sold today', value: carsSold, icon: FiKey,
      // What CAP KEEPS, not what the buyer paid — on a commission car those
      // differ by lakhs and the wrong one reads like a record day.
      sub: `${fmtMoney(today?.cars_earning_value) || '₹0'} earned`,
      to: ROUTES.CARS, cta: 'See the lot',
    });
  }
  if (carsFloat > 0) {
    cells.push({
      label: "Cars holds of CAP's", value: fmtMoney(carsFloat) || '₹0', icon: FiKey,
      sub: 'given and not back yet', to: ROUTES.CARS, cta: 'See the float',
    });
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {cells.map((c) => (
        <Link
          key={c.label}
          to={c.to}
          className={`group bg-white rounded-2xl border shadow-sm p-4 transition-all hover:shadow-md ${
            c.accent
              ? 'border-violet-200 hover:border-violet-300'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="flex items-center gap-2 text-slate-400 mb-1.5">
            <c.icon className={`w-4 h-4 ${c.accent ? 'text-violet-500' : ''}`} />
            <span className="text-[11px] font-semibold uppercase tracking-wide truncate">{c.label}</span>
          </div>
          <p className="text-2xl font-black text-slate-900 leading-none tabular-nums">{c.value}</p>
          <p className="text-xs text-slate-400 mt-1">{c.sub}</p>
          <span className={`mt-2 inline-flex items-center gap-1 text-[11px] font-bold ${
            c.accent ? 'text-violet-600' : 'text-slate-400 group-hover:text-slate-600'
          }`}>
            {c.cta}
            <FiArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </Link>
      ))}
    </div>
  );
};

// The screen itself, kept SEPARATE from the page shell on purpose.
//
// WorklistProvider lives inside MainLayout. If this hook were called in the
// same component that renders <MainLayout>, the provider would be a DESCENDANT
// of its own consumer, so useWorklist() would read the context default
// ({data: null, loading: false, error: null}) and never see the API response —
// every count silently rendered 0, "You are all clear" showed permanently, and
// Refresh did nothing because the default refresh is a no-op. No error appeared
// anywhere, because as far as this component was concerned nothing had failed.
const TodayBoard = () => {
  const { data, loading, error, refresh } = useWorklist();

  const queues = data?.queues || {};
  const pending = WORK_QUEUES
    .map((q) => ({ queue: q, stat: queues[q.key] }))
    .filter((r) => Number(r.stat?.count || 0) > 0);
  const clear = WORK_QUEUES.filter((q) => Number(queues[q.key]?.count || 0) === 0);
  const total = Number(data?.total_actions || 0);

  return (
    <>
      <PageHeader
        icon={FiSun}
        title="Today"
        purpose={
          total > 0
            ? `${total} thing${total === 1 ? '' : 's'} need you. Work down the list — it is in the order money moves.`
            : 'Everything that needs you shows up here first.'
        }
        meta={data?.generated_at ? `As of ${data.generated_at}` : null}
      >
        <button
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-60"
        >
          <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </PageHeader>

      {error && (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="space-y-5">
        <TodaySnapshot today={data?.today} />

        {/* Nothing waiting is a real answer — say it, don't show an empty grid */}
        {!loading && total === 0 && (
          <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
              <FiCheckCircle className="w-7 h-7 text-emerald-500" />
            </div>
            <p className="text-lg font-black text-slate-800">You are all clear</p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              No payments to verify, nothing to dispatch, no refunds or vendor dues open.
              Anything new will appear here the moment it does.
            </p>
            <Link
              to={ROUTES.DASHBOARD}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all"
            >
              <FiBarChart2 className="w-4 h-4" /> See how the business is doing
            </Link>
          </div>
        )}

        {pending.length > 0 && (
          <div>
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
              Needs you now
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pending.map(({ queue, stat }) => (
                <QueueCard key={queue.key} queue={queue} stat={stat} />
              ))}
            </div>
          </div>
        )}

        {/* Cleared queues stay visible but quiet — so "no refunds pending" is
            something you can SEE, not something you have to go and check. */}
        {clear.length > 0 && total > 0 && (
          <div>
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
              Nothing pending
            </h2>
            <div className="flex flex-wrap gap-2">
              {clear.map((q) => (
                <Link
                  key={q.key}
                  to={q.path}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700 transition-all"
                >
                  <FiCheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                  {q.title}
                </Link>
              ))}
            </div>
          </div>
        )}

        {loading && !data && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 rounded-2xl bg-white border border-slate-200 animate-pulse" />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// The shell mounts the provider; TodayBoard reads it from underneath. Keep
// these two apart — collapsing them back into one component silently returns
// the whole screen to zeros.
const TodayPage = () => (
  <MainLayout>
    <TodayBoard />
  </MainLayout>
);

export default TodayPage;
